import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useServiceTimes } from '../hooks/useServiceTimes';
import { useSiteContent } from '../hooks/useSiteContent';
import { SafeChurchBadge } from './SafeChurchBadge';

const Footer = () => {
  const services = useServiceTimes();
  const site = useSiteContent();

  // Check if we have any contact info to show
  const hasAddress = site.address_line1 || site.address_line2;
  const hasContactInfo = hasAddress || site.phone || site.email;

  return (
    <footer className="bg-church-green text-white py-8 sm:py-12" data-testid="site-footer">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
          <div className="col-span-2 md:col-span-1">
            <img src="/logo-white.png" alt="Life Church of New Jersey logo" className="h-12 sm:h-16 mb-3 sm:mb-4" loading="lazy" />
            <p className="text-green-100 text-xs sm:text-sm">
              A Multi-Generational, Multicultural, Apostolic Church in Laurence Harbor, NJ.
            </p>
            <SafeChurchBadge variant="onDark" testId="footer-safe-church-badge" className="mt-3" />
          </div>
          <div>
            <h4 className="font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm text-green-100">
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/beliefs" className="hover:text-white transition-colors">Our Beliefs</Link></li>
              <li><Link to="/ministries" className="hover:text-white transition-colors">Ministries</Link></li>
              <li><Link to="/what-to-expect" className="hover:text-white transition-colors">What to Expect</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4">Service Times</h4>
            <ul className="space-y-2 text-sm text-green-100">
              {services.length > 0 ? (
                services.map((s) => (
                  <li key={s.id || `${s.day}-${s.time}`}>{s.day}: {s.time}</li>
                ))
              ) : (
                <li className="text-green-200/60">Contact us for times</li>
              )}
            </ul>
          </div>
          {hasContactInfo && (
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-3 text-sm text-green-100">
                {hasAddress && (
                  <li className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span data-testid="footer-address">
                      {site.address_line1}{site.address_line1 && site.address_line2 && <br />}{site.address_line2}
                    </span>
                  </li>
                )}
                {site.phone && (
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <a href={`tel:${site.phone}`} className="hover:text-white transition-colors" data-testid="footer-phone">
                      {site.phone}
                    </a>
                  </li>
                )}
                {site.email && (
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <a href={`mailto:${site.email}`} className="hover:text-white transition-colors" data-testid="footer-email">
                      {site.email}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
        <div className="border-t border-green-700 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-green-100">
          <p>&copy; {new Date().getFullYear()} Life Church of New Jersey. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
